<template>
  <div class="flex flex-col w-full bg-white p-5 mt-6 mb-6 rounded shadow-md">
    <!-- Title and Header Section -->
    <div class="flex items-center mb-4">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">
        Calibration
      </h3>
      <hr class="flex-grow border-primary border-t-2 mr-4"/>

      <div class="flex items-center">
        <label class="text-sm mr-2">Ambient Temperature:</label>
        <input
            v-model="form.ambient_temperature"
            type="text"
            class="rounded-l bge-input bge-input-spacing"
            placeholder="°C"
            style="text-align: right; width: 200px;"
        />
      </div>
    </div>

    <!-- Calibration Data Table -->
    <div class="bg-gray-100 p-4 rounded-md">
      <div
          class="grid grid-cols-5 rounded-md text-center font-medium bg-gray-200 p-2 uppercase font-semibold"
          style="column-gap: 40px;"
      >
        <div>Inclination Applied (%)</div>
        <div>Initial Reading (%)</div>
        <div class="flex justify-center items-center space-x-2">
          <h2 class="text-center">Final Reading (%)</h2>
          <AppButton color="green" label="" @click="copyInitialToFinal" :icon="true" class="mr-2">
            <template #icon>
              <Icon iconType="copy-plus" />
            </template>
          </AppButton>
        </div>
        <div>% Error</div>
        <div>Max Tolerance (%)</div>
      </div>

      <!-- Data Rows -->
      <div
          v-for="(item, index) in form.calibration"
          :key="index"
          class="grid grid-cols-5 text-center mt-4"
          style="column-gap: 40px;"
      >
        <input type="text" v-model="item.inclination_applied" class="input-style text-gray-500" readonly />
        <input type="text" v-model="item.initial_reading" class="input-style" />
        <input type="text" v-model="item.final_reading" class="input-style" />
        <input type="text" v-model="item.percent_error" class="input-style" />
        <input type="text" v-model="item.max_tolerance" class="input-style text-gray-500" readonly />
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import notificationMixin from "@/mixins/notificationMixin";

function blankForm() {
  return {
    ambient_temperature: 0,
    calibration: [
      { inclination_applied: 0,   initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "0.00 - 0.00" },
      { inclination_applied: 16,  initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "15.52 - 16.48" },
      { inclination_applied: 25,  initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "24.25 - 25.75" },
      { inclination_applied: 50,  initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "48.50 - 51.50" },
      { inclination_applied: 80,  initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "77.60 - 82.40" },
      { inclination_applied: 100, initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "98.00 - 103.00" }
    ]
  };
}

export default {
  name: "Decelerometer",
  components: { AppButton, Icon },
  mixins: [notificationMixin],
  props: {
    calibration_details: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["update:calibration_details"],
  data() {
    return {
      form: blankForm(),
      hasUserEdited: false
    };
  },
  mounted() {
    this.seedFrom(this.calibration_details);
  },
  watch: {
    calibration_details: {
      deep: true,
      handler(val) {
        if (!val) return;
        if (this.hasUserEdited) return;
        this.seedFrom(val);
      }
    },
    form: {
      deep: true,
      handler(v) {
        this.hasUserEdited = true;
        this.$emit("update:calibration_details", JSON.parse(JSON.stringify(v)));
      }
    }
  },
  methods: {
    seedFrom(src) {
      const safe = src && Object.keys(src).length ? src : blankForm();
      const rows = Array.isArray(safe.calibration) ? safe.calibration : blankForm().calibration;

      this.form = {
        ambient_temperature: typeof safe.ambient_temperature !== "undefined" ? safe.ambient_temperature : 0,
        calibration: rows.map((r, idx) => {
          const defaults = blankForm().calibration[idx] || {
            inclination_applied: 0, initial_reading: 0, final_reading: 0, percent_error: 0, max_tolerance: "0.00 - 0.00"
          };
          return {
            inclination_applied: typeof r.inclination_applied !== "undefined" ? r.inclination_applied : defaults.inclination_applied,
            initial_reading:     typeof r.initial_reading     !== "undefined" ? r.initial_reading     : defaults.initial_reading,
            final_reading:       typeof r.final_reading       !== "undefined" ? r.final_reading       : defaults.final_reading,
            percent_error:       typeof r.percent_error       !== "undefined" ? r.percent_error       : defaults.percent_error,
            max_tolerance:       typeof r.max_tolerance       !== "undefined" ? r.max_tolerance       : defaults.max_tolerance
          };
        })
      };

      this.hasUserEdited = false;
    },

    getCurrentData() {
      return JSON.parse(JSON.stringify(this.form));
    },

    async copyInitialToFinal() {
      const ok = await this.confirmAction({
        message: "Do you want to copy initial calibration values to final calibration for the Decelerometer?",
        header: "Confirm Copy",
        acceptLabel: "Yes",
        rejectLabel: "No"
      });
      if (!ok) return;

      this.form.calibration.forEach(entry => {
        entry.final_reading = entry.initial_reading;
      });
    }
  }
};
</script>

<style scoped>
.input-style {
  padding: 0.5rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 0.25rem;
  width: 100%;
  text-align: center;
}
</style>
